import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import WarningRoundedIcon from '@mui/icons-material/WarningRounded'
import type { SvgIconComponent } from '@mui/icons-material'
import { Button } from '@mui/material'
import React from 'react'
import { Link } from 'react-router-dom'
import Drawer from '../../../components/Drawer'
import GrantContent from '../../../components/GrantContent'
import * as Permisos from '../../../utils/Permisos'
import PolicyClose from '../close/policy-close'
import {
    BITACORA_FILTERS,
    chipClass,
    formatActivityDate,
    formatCompactDate,
    formatPct,
    formatSpokenDate,
    isBusy,
    labelize,
    money,
    NOTE_TYPES,
    noteMeta,
    personName,
    RamoMark,
    ramoName,
    vigenciaLabel,
} from '../common/policy-common'
import {
    aseguradoraName,
    extTone,
    fileExt,
    reciboChip,
    reciboPaidOn,
    siniestroChip,
    usePolicyDrawerModel,
    vehicleLabel,
} from './policy-drawer-model'
import type { PolicyDrawerProps } from './policy-drawer-model'

export type { PolicyDrawerProps, PolicyDrawerTab } from './policy-drawer-model'

const actionClass = 'inline-flex h-[38px] items-center gap-1.5 rounded-[10px] border border-k-line-strong bg-transparent px-3 text-[13px] font-bold text-k-ink no-underline hover:bg-k-hover disabled:cursor-not-allowed disabled:opacity-40'

const Field = ({ label, children, wide }: { label: string; children?: React.ReactNode; wide?: boolean }) => (
    <div className={wide ? 'sm:col-span-2' : ''}>
        <dt className="mb-0.5 text-xs text-k-muted">{label}</dt>
        <dd className="m-0 break-words text-sm font-semibold text-k-ink">{children || '—'}</dd>
    </div>
)

const Section = ({ title, icon: Icon, children }: { title: string; icon: SvgIconComponent; children: React.ReactNode }) => (
    <section className="flex flex-col gap-3 rounded-2xl border border-k-line bg-k-surface px-[18px] py-4">
        <h3 className="m-0 flex items-center gap-2 text-[13px] font-extrabold uppercase tracking-[0.06em] text-k-muted">
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-k-accent-soft text-k-accent-ink" aria-hidden="true">
                <Icon style={{ fontSize: 16 }} />
            </span>
            {title}
        </h3>
        <dl className="m-0 grid grid-cols-1 gap-x-[18px] gap-y-3 sm:grid-cols-2">{children}</dl>
    </section>
)

const Pulse = ({ className }: { className: string }) => <div className={`animate-pulse rounded bg-k-line ${className}`} />

const EmptyState = ({ title, hint }: { title: string; hint?: string }) => (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-k-line bg-k-surface px-4 py-10 text-center">
        <p className="m-0 text-sm font-bold text-k-ink">{title}</p>
        {hint && <p className="mb-0 mt-1 text-xs text-k-muted">{hint}</p>}
    </div>
)

const PolicyDrawer = (props: PolicyDrawerProps) => {
    const { polizaId, onClose } = props
    const model = usePolicyDrawerModel(props)
    const { poliza, loading, cliente, name, currency, recibos, status, vigencia, restan, pago } = model

    return (
        <>
            <Drawer open={Boolean(polizaId)} onClose={onClose} bare width={680}>
                <div className="flex h-full min-h-0 flex-col" role="dialog" aria-modal="true" aria-label={poliza?.noPoliza ? `Detalle de póliza ${poliza.noPoliza}` : 'Detalle de póliza'}>
                    <div className="flex shrink-0 flex-col gap-4 border-b border-k-line px-6 pt-5">
                        <div className="flex items-start gap-3.5">
                            <RamoMark ramo={poliza?.ramo} boxed large />
                            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                                <div className="flex flex-wrap items-center gap-1">
                                    <h2 className="m-0 truncate text-[21px] font-extrabold tracking-[-0.02em] text-k-ink">{poliza?.noPoliza || 'Detalle de póliza'}</h2>
                                    {poliza?.noPoliza && (
                                        <button type="button" aria-label="Copiar número de póliza" className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-lg text-k-muted hover:bg-k-hover" onClick={model.copyNumber}>
                                            <ContentCopyOutlinedIcon style={{ fontSize: 15 }} />
                                        </button>
                                    )}
                                </div>
                                {poliza && !loading && status && pago && (
                                    <>
                                        <span className="text-sm font-semibold text-k-ink-2">{poliza.concepto || ramoName(poliza.ramo) || 'Póliza'}</span>
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-k-muted">
                                            <span>{aseguradoraName(poliza)}</span>
                                            {poliza.paquete && <span>{poliza.paquete}</span>}
                                            <span>
                                                Asegurado:{' '}
                                                {cliente?.id ? (
                                                    <Link to={`/clientes/${cliente.id}`} className="font-bold text-k-accent-ink no-underline hover:underline">{name || '—'}</Link>
                                                ) : (name || '—')}
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            <span className={`rounded-full px-[9px] py-[3px] text-xs font-bold ${status.className}`}>{status.label}</span>
                                            <span className={`rounded-full px-[9px] py-[3px] text-xs font-bold ${pago.className}`}>{pago.label}</span>
                                            {poliza.esRobot && <span className="rounded-full bg-tone-orange px-[9px] py-[3px] text-xs font-bold text-tone-orange-ink">Por validar</span>}
                                        </div>
                                    </>
                                )}
                            </div>
                            <button type="button" aria-label="Cerrar" className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-k-line bg-transparent text-k-ink hover:bg-k-hover" onClick={onClose}>
                                <CloseIcon style={{ fontSize: 18 }} />
                            </button>
                        </div>

                        {poliza && !loading && (
                            <div className="flex flex-wrap justify-end gap-2">
                                <button type="button" className={actionClass} onClick={model.openCaratula}>
                                    <DownloadOutlinedIcon style={{ fontSize: 15 }} />Carátula
                                </button>
                                <GrantContent permisos={[Permisos.POLIZAS_D]}>
                                    <button type="button" className={actionClass} onClick={model.openClose}>
                                        <CloseIcon style={{ fontSize: 15 }} />Cerrar
                                    </button>
                                </GrantContent>
                                <GrantContent permisos={[Permisos.POLIZAS_W]}>
                                    <button
                                        type="button"
                                        className={actionClass}
                                        disabled={!model.canRenew}
                                        onClick={model.renewPolicy}
                                    >
                                        <AutorenewOutlinedIcon style={{ fontSize: 15 }} />Renovar
                                    </button>
                                </GrantContent>
                            </div>
                        )}

                        <div role="tablist" aria-label="Secciones de la póliza" className="-mx-2 flex gap-0.5 overflow-x-auto">
                            {model.tabs.map((item) => {
                                const selected = model.tab === item.id
                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        role="tab"
                                        aria-selected={selected}
                                        className={`inline-flex min-h-12 items-center gap-2 whitespace-nowrap border-0 border-b-[3px] bg-transparent px-3 text-sm ${selected ? 'border-k-primary font-extrabold text-k-ink' : 'border-transparent font-semibold text-k-muted'}`}
                                        onClick={() => model.setTab(item.id)}
                                    >
                                        {item.label}
                                        {item.count != null && (
                                            <span className={`inline-flex h-5 min-w-[22px] items-center justify-center rounded-[10px] px-1.5 text-xs font-bold ${selected ? 'bg-k-accent-soft text-k-accent-ink' : 'bg-k-line text-k-ink-2'}`}>
                                                {item.count}
                                            </span>
                                        )}
                                    </button>
                                )
                            })}
                        </div>
                    </div>

                    <div role="tabpanel" className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-k-bg px-6 py-5">
                        {loading && (
                            <div className="flex flex-col items-center justify-center gap-4 py-12">
                                <RefreshIcon className="animate-spin text-k-primary" style={{ fontSize: 40 }} />
                                <p className="text-sm text-k-muted">Cargando detalle...</p>
                            </div>
                        )}

                        {model.error && !loading && (
                            <div className="rounded-lg border border-k-danger/30 bg-k-danger-soft p-4">
                                <div className="flex items-center gap-2">
                                    <ErrorOutlineIcon className="text-k-danger" style={{ fontSize: 20 }} />
                                    <p className="text-sm text-k-danger-ink">{model.error}</p>
                                </div>
                                <Button className="mt-3" variant="contained" onClick={model.reload}>Reintentar</Button>
                            </div>
                        )}

                        {poliza && !loading && model.tab === 'general' && vigencia && restan && (
                            <>
                                {poliza.esRobot && (
                                    <div className="flex items-start gap-2 rounded-lg bg-tone-orange px-4 py-3 text-tone-orange-ink">
                                        <WarningRoundedIcon style={{ fontSize: 18 }} />
                                        <p className="text-sm">Póliza creada automáticamente con la sincronización. Conviene validar sus datos.</p>
                                    </div>
                                )}
                                <section className="flex flex-col gap-2.5 rounded-2xl border border-k-line bg-k-surface px-[18px] py-4">
                                    <div className="flex items-baseline justify-between gap-2.5">
                                        <span className="text-[13px] font-bold text-k-muted">Vigencia</span>
                                        {restan.text && <span className={`text-[13px] font-bold ${restan.urgent ? 'text-tone-orange-ink' : 'text-k-muted'}`}>{restan.text}</span>}
                                    </div>
                                    <div className="flex justify-between text-sm font-bold tabular-nums">
                                        <span>{formatCompactDate(poliza.fechaVigenciaDe) || '—'}</span>
                                        <span>{formatCompactDate(poliza.fechaVigenciaA) || '—'}</span>
                                    </div>
                                    <span className="h-2 overflow-hidden rounded bg-k-line" role="img" aria-label={`Avance de vigencia ${vigencia.progress}%`}>
                                        <span className={`block h-full rounded ${vigencia.barClass}`} style={{ width: `${vigencia.progress}%` }} />
                                    </span>
                                </section>
                                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                                    <div className="flex flex-col gap-1 rounded-[14px] border border-k-line bg-k-surface px-3.5 py-3">
                                        <span className="text-xs font-semibold text-k-muted">Prima total</span>
                                        <span className="text-lg font-extrabold tabular-nums">{money(poliza.total, currency)}</span>
                                        <span className="text-xs font-semibold text-k-muted">{model.forma ? `${model.forma}${recibos.length ? ` · ${recibos.length} recibos` : ''}` : ' '}</span>
                                    </div>
                                    <div className="flex flex-col gap-1 rounded-[14px] border border-k-line bg-k-surface px-3.5 py-3">
                                        <span className="text-xs font-semibold text-k-muted">Próximo recibo</span>
                                        <span className="text-[15px] font-extrabold leading-7 tabular-nums">{model.nextReceipt ? vigenciaLabel(model.nextReceipt.fecVigDe, model.nextReceipt.fecVigA) : '—'}</span>
                                        <span className="text-xs font-semibold text-k-muted">{model.nextReceipt ? `Recibo ${model.nextReceipt.serie}/${model.nextReceipt.totalSerie} · ${money(model.nextReceipt.total, currency)}` : 'Sin recibos pendientes'}</span>
                                    </div>
                                    <div className="flex flex-col gap-1 rounded-[14px] border border-k-line bg-k-surface px-3.5 py-3">
                                        <span className="text-xs font-semibold text-k-muted">Siniestros</span>
                                        <span className="text-lg font-extrabold tabular-nums">{model.siniestrosState === 'ready' ? model.siniestros.length : (isBusy(model.siniestrosState) ? '…' : '—')}</span>
                                        <span className="text-xs font-semibold text-k-ok">{model.siniestrosState === 'ready' ? (model.siniestros.length ? 'Registrados en la póliza' : 'Sin siniestros') : ' '}</span>
                                    </div>
                                </div>
                                <Section title="Datos de la póliza" icon={DescriptionOutlinedIcon}>
                                    <Field label="Número">{poliza.noPoliza}</Field>
                                    <Field label="Ramo">{ramoName(poliza.ramo)}</Field>
                                    <Field label="Aseguradora">{aseguradoraName(poliza)}</Field>
                                    <Field label="Plan">{poliza.paquete}</Field>
                                    <Field label="Moneda">{currency}</Field>
                                    <Field label="Clave de agente">{poliza.agenteClave?.clave}</Field>
                                </Section>
                                <Section title="Contratante y asegurado" icon={PeopleAltOutlinedIcon}>
                                    <Field label="Contratante">{name}</Field>
                                    <Field label="RFC">{cliente?.rfc}</Field>
                                    <Field label="Vendedor">{poliza.vendedor ? personName(poliza.vendedor) : null}</Field>
                                    <Field label="Asegurados">{model.asegurados}</Field>
                                </Section>
                                {poliza.vehiculo && (
                                    <Section title="Bien asegurado" icon={DirectionsCarOutlinedIcon}>
                                        <Field label="Vehículo" wide>{vehicleLabel(poliza.vehiculo)}</Field>
                                        <Field label="Placas">{poliza.vehiculo.placas}</Field>
                                        <Field label="Número de serie">{poliza.vehiculo.noSerie}</Field>
                                        <Field label="Descripción">{poliza.vehiculo.descripcion}</Field>
                                        <Field label="AMIS">{poliza.vehiculo.amis}</Field>
                                    </Section>
                                )}
                                <Section title="Forma de pago" icon={PaymentsOutlinedIcon}>
                                    <Field label="Periodicidad">{model.forma}{recibos.length ? ` · ${recibos.length} recibos` : ''}</Field>
                                    <Field label="Conducto">{model.conducto}</Field>
                                    <Field label="Prima total">{money(poliza.total, currency)}</Field>
                                    <Field label="Recargo">{money(poliza.recargo, currency)}</Field>
                                </Section>
                                <Section title="Comisiones" icon={PercentOutlinedIcon}>
                                    <Field label="Porcentaje">{model.comisionPct != null ? `${formatPct(model.comisionPct)}%` : null}</Field>
                                    <Field label="Comisión total">{model.comisionTotal != null ? money(model.comisionTotal, currency) : null}</Field>
                                    <Field label="Cobrada · recibos pagados">{money(model.comisionCobrada, currency)}</Field>
                                    <Field label="Por cobrar · recibos pendientes">{money(model.comisionPorCobrar, currency)}</Field>
                                </Section>
                            </>
                        )}

                        {poliza && !loading && model.tab === 'recibos' && (
                            <>
                                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                                    <div className="flex flex-col gap-1 rounded-[14px] border border-k-line bg-k-surface px-3.5 py-3">
                                        <span className="text-xs font-semibold text-k-muted">Pagados</span>
                                        <span className="text-lg font-extrabold tabular-nums text-k-ok">{model.paidCount} de {recibos.length}</span>
                                    </div>
                                    <div className="flex flex-col gap-1 rounded-[14px] border border-k-line bg-k-surface px-3.5 py-3">
                                        <span className="text-xs font-semibold text-k-muted">Pendiente</span>
                                        <span className="text-lg font-extrabold tabular-nums">{money(model.pendingTotal, currency)}</span>
                                    </div>
                                    <div className="flex flex-col gap-1 rounded-[14px] border border-k-line bg-k-surface px-3.5 py-3">
                                        <span className="text-xs font-semibold text-k-muted">Vencido</span>
                                        <span className={`text-lg font-extrabold tabular-nums ${model.overdueCount ? 'text-k-danger-ink' : 'text-k-ok'}`}>{money(model.overdueTotal, currency)}</span>
                                    </div>
                                </div>
                                {recibos.length === 0 && <EmptyState title="No hay recibos" hint="Esta póliza aún no tiene recibos." />}
                                {recibos.length > 0 && (
                                    <section className="rounded-2xl border border-k-line bg-k-surface px-4">
                                        {recibos.map((recibo) => {
                                            const chip = reciboChip(recibo)
                                            return (
                                                <div key={recibo.id} className="flex flex-wrap items-center gap-x-3.5 gap-y-1.5 border-t border-k-line py-3 first:border-t-0">
                                                    <span className="flex min-w-[180px] flex-1 flex-col gap-0.5">
                                                        <span className="text-sm font-bold">Recibo {recibo.serie}/{recibo.totalSerie}</span>
                                                        <span className="text-xs tabular-nums text-k-muted">Vigencia {vigenciaLabel(recibo.fecVigDe, recibo.fecVigA)}</span>
                                                        <span className="text-xs text-k-muted">{reciboPaidOn(recibo)}{recibo.folioPago ? ` · ${recibo.folioPago}` : ''}</span>
                                                    </span>
                                                    <span className="flex min-w-[90px] flex-col items-end gap-0.5 tabular-nums">
                                                        <span className="text-sm font-bold">{money(recibo.total, currency)}</span>
                                                        <span className="text-xs text-k-muted">Comisión {money(recibo.comision, currency)}</span>
                                                    </span>
                                                    <span className={`min-w-[76px] rounded-full px-[9px] py-[3px] text-center text-xs font-bold ${chip.className}`}>{chip.label}</span>
                                                </div>
                                            )
                                        })}
                                    </section>
                                )}
                            </>
                        )}

                        {poliza && !loading && model.tab === 'siniestros' && (
                            <>
                                {isBusy(model.siniestrosState) && (
                                    <div className="flex flex-col gap-2" aria-busy="true" aria-label="Cargando siniestros">
                                        {[1, 2, 3].map((row) => <Pulse key={row} className="h-16 w-full rounded-2xl" />)}
                                    </div>
                                )}
                                {model.siniestrosError && <p className="m-0 text-sm text-k-danger-ink">{model.siniestrosError}</p>}
                                {model.siniestrosState === 'ready' && model.siniestros.length === 0 && (
                                    <EmptyState title="Sin siniestros" hint="Esta póliza no tiene siniestros registrados." />
                                )}
                                {model.siniestrosState === 'ready' && model.siniestros.map((item) => (
                                    <div key={item.id} className="flex flex-wrap items-start justify-between gap-2.5 rounded-2xl border border-k-line bg-k-surface px-[18px] py-4">
                                        <span className="flex min-w-[200px] flex-1 flex-col gap-0.5">
                                            <span className="text-[15px] font-extrabold">{item.descripcion || 'Siniestro'}</span>
                                            <span className="text-[13px] tabular-nums text-k-muted">{item.noSiniestro || '—'} · {formatSpokenDate(item.fechaSiniestro) || '—'}</span>
                                        </span>
                                        <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${siniestroChip(item.status)}`}>{labelize(item.status) || '—'}</span>
                                    </div>
                                ))}
                            </>
                        )}

                        {poliza && !loading && model.tab === 'bitacora' && (
                            <>
                                <GrantContent permisos={[Permisos.BITACORA_W]}>
                                    <section className="flex flex-col gap-2.5 rounded-2xl border border-k-line bg-k-surface p-3.5">
                                        <div role="radiogroup" aria-label="Tipo de registro" className="flex flex-wrap gap-1.5">
                                            {NOTE_TYPES.map((item) => (
                                                <button key={item.id} type="button" role="radio" aria-checked={model.noteType === item.id} className={chipClass(model.noteType === item.id)} onClick={() => model.setNoteType(item.id)}>
                                                    <item.Icon style={{ fontSize: 16 }} />
                                                    {item.label}
                                                </button>
                                            ))}
                                        </div>
                                        <label className="flex flex-col">
                                            <span className="sr-only">Nueva nota de la póliza</span>
                                            <textarea rows={3} value={model.note} placeholder={model.notePlaceholder} className="resize-y rounded-xl border border-k-line-strong bg-k-bg px-3 py-2.5 text-sm text-k-ink" onChange={(event) => model.setNote(event.target.value)} />
                                        </label>
                                        <div className="flex justify-end">
                                            <button type="button" disabled={!model.note.trim() || model.savingNote} className={`h-10 rounded-[10px] border-0 px-3.5 text-[13px] font-bold ${model.note.trim() && !model.savingNote ? 'cursor-pointer bg-k-primary text-white' : 'cursor-not-allowed bg-k-line text-k-muted-2'}`} onClick={model.addNote}>
                                                {model.savingNote ? 'Guardando…' : 'Agregar a bitácora'}
                                            </button>
                                        </div>
                                    </section>
                                </GrantContent>
                                <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrar bitácora">
                                    {BITACORA_FILTERS.map((item) => (
                                        <button key={item.id} type="button" aria-pressed={model.bitacoraFilter === item.id} className={chipClass(model.bitacoraFilter === item.id)} onClick={() => model.setBitacoraFilter(item.id)}>
                                            {item.label}
                                        </button>
                                    ))}
                                </div>
                                {isBusy(model.bitacoraState) && (
                                    <div className="flex flex-col" aria-busy="true" aria-label="Cargando bitácora">
                                        {[1, 2, 3].map((row) => <Pulse key={row} className="mb-2 h-16 w-full rounded-xl" />)}
                                    </div>
                                )}
                                {model.bitacoraError && <p className="m-0 text-sm text-k-danger-ink">{model.bitacoraError}</p>}
                                {model.bitacoraState === 'ready' && model.visibleNotes.length === 0 && (
                                    <EmptyState title="No hay registros" hint={model.bitacoraFilter === 'todo' ? 'Esta póliza aún no tiene bitácora.' : 'No hay registros en esta categoría.'} />
                                )}
                                {model.bitacoraState === 'ready' && model.visibleNotes.length > 0 && (
                                    <div className="flex flex-col">
                                        {model.visibleNotes.map((item, index) => {
                                            const meta = noteMeta(item.tipo)
                                            return (
                                                <div key={item.id || index} className="flex gap-3.5">
                                                    <div className="flex flex-col items-center">
                                                        <span className={`flex h-[34px] w-[34px] items-center justify-center rounded-[10px] ${meta.tone}`}>
                                                            <meta.Icon style={{ fontSize: 16 }} />
                                                        </span>
                                                        {index < model.visibleNotes.length - 1 && <span className="my-1 w-0.5 flex-1 bg-k-line" />}
                                                    </div>
                                                    <div className="flex min-w-0 flex-1 flex-col gap-1 pb-[18px] pt-1">
                                                        <div className="flex flex-wrap justify-between gap-x-2.5 gap-y-1">
                                                            <span className="text-sm font-extrabold">{meta.label}</span>
                                                            <span className="text-xs tabular-nums text-k-muted">{formatActivityDate(item.fecha) || '—'}</span>
                                                        </div>
                                                        {item.texto && (
                                                            <div
                                                                className="m-0 break-words rounded-xl border border-k-line bg-k-surface px-3 py-2.5 text-sm leading-normal text-k-ink-2 [&_a]:text-k-accent-ink [&_a]:underline [&_img]:max-w-full [&_p]:m-0 [&_p+p]:mt-2"
                                                                dangerouslySetInnerHTML={{ __html: String(item.texto).replace(/\n/g, '<br/>') }}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </>
                        )}

                        {poliza && !loading && model.tab === 'adjuntos' && (
                            <>
                                <GrantContent permisos={[Permisos.ADJUNTOS_W]}>
                                    <label className={`flex cursor-pointer items-center gap-3.5 rounded-2xl border-[1.5px] border-dashed border-k-line-strong bg-k-surface p-4 ${model.uploading ? 'opacity-60' : ''}`}>
                                        <span className="flex h-11 w-11 items-center justify-center rounded-[14px] bg-tone-blue text-tone-blue-ink">
                                            <UploadFileOutlinedIcon />
                                        </span>
                                        <span className="flex flex-col gap-0.5">
                                            <span className="text-sm font-extrabold text-k-ink">{model.uploading ? 'Subiendo archivos…' : 'Subir archivos a la póliza'}</span>
                                            <span className="text-xs text-k-muted">Carátulas, endosos, facturas · máx. 10 MB</span>
                                        </span>
                                        <input type="file" multiple className="sr-only" disabled={model.uploading} onChange={(event) => { model.uploadFiles(event.target.files); event.target.value = '' }} />
                                    </label>
                                </GrantContent>
                                {isBusy(model.adjuntosState) && (
                                    <div className="flex flex-col gap-2" aria-busy="true">
                                        {[1, 2, 3].map((row) => <Pulse key={row} className="h-16 w-full rounded-[14px]" />)}
                                    </div>
                                )}
                                {model.adjuntosError && <p className="m-0 text-sm text-k-danger-ink">{model.adjuntosError}</p>}
                                {model.adjuntosState === 'ready' && model.adjuntos.length === 0 && <EmptyState title="No hay adjuntos" hint="Esta póliza aún no tiene archivos." />}
                                {model.adjuntosState === 'ready' && model.adjuntos.length > 0 && (
                                    <div className="flex flex-col gap-2">
                                        {model.adjuntos.map(({ adjunto, tag, fromRecibo }) => {
                                            const ext = fileExt(adjunto.archivo)
                                            return (
                                                <div key={adjunto.id} className="flex items-center gap-3 rounded-[14px] border border-k-line bg-k-surface px-3.5 py-3">
                                                    <span className={`flex h-11 w-9 items-end justify-center rounded-[7px] pb-[5px] text-[10px] font-extrabold ${extTone(ext)}`}>{ext}</span>
                                                    <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
                                                        <span className="truncate text-sm font-bold">{adjunto.archivo || 'Archivo'}</span>
                                                        <span className="text-xs text-k-muted">{formatActivityDate(adjunto.fecha) || '—'}</span>
                                                    </span>
                                                    <span className={`hidden whitespace-nowrap rounded-full px-[9px] py-[3px] text-[11px] font-bold sm:inline ${fromRecibo ? 'bg-k-ok-soft text-k-ok' : 'bg-k-line text-k-ink-2'}`}>{tag}</span>
                                                    <button type="button" aria-label={`Ver ${adjunto.archivo}`} className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] border border-k-line bg-transparent text-k-ink" onClick={() => model.openFile(adjunto.path)}>
                                                        <VisibilityOutlinedIcon style={{ fontSize: 16 }} />
                                                    </button>
                                                    <button type="button" aria-label={`Descargar ${adjunto.archivo}`} className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] border border-k-line bg-transparent text-k-ink" onClick={() => model.openFile(adjunto.path)}>
                                                        <FileDownloadOutlinedIcon style={{ fontSize: 16 }} />
                                                    </button>
                                                    <GrantContent permisos={[Permisos.ADJUNTOS_D]}>
                                                        {model.confirmDeleteId === adjunto.id ? (
                                                            <button type="button" className="h-9 rounded-[10px] border border-k-danger px-2 text-xs font-bold text-k-danger" onClick={() => model.deleteAdjunto(adjunto.id)}>Eliminar</button>
                                                        ) : (
                                                            <button type="button" aria-label={`Eliminar ${adjunto.archivo}`} className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] border border-k-line bg-transparent text-k-danger" onClick={() => model.setConfirmDeleteId(adjunto.id)}>
                                                                <DeleteOutlinedIcon style={{ fontSize: 16 }} />
                                                            </button>
                                                        )}
                                                    </GrantContent>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="flex shrink-0 flex-wrap items-center justify-between gap-2.5 border-t border-k-line bg-k-surface px-6 py-3.5">
                        <span className="text-xs text-k-muted">{formatSpokenDate(poliza?.fechaCreacion) ? `Emitida ${formatSpokenDate(poliza?.fechaCreacion)}` : ''}</span>
                        <div className="flex gap-2">
                            <button type="button" className="h-11 rounded-xl border border-k-line-strong bg-transparent px-4 text-sm font-bold text-k-ink" onClick={onClose}>Cerrar</button>
                            {poliza?.id && (
                                <Link to={`/polizas/${poliza.id}`} className="inline-flex h-11 items-center gap-1.5 rounded-xl bg-k-accent-soft px-4 text-sm font-extrabold text-k-accent-ink no-underline">
                                    Abrir póliza completa
                                    <OpenInNewIcon style={{ fontSize: 15 }} />
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </Drawer>
            <PolicyClose
                polizaId={model.closing ? polizaId : null}
                onClose={model.cancelClose}
                onCompleted={model.closeCompleted}
            />
            {model.toast && (
                <div role="status" className="fixed bottom-6 left-6 z-[60] flex items-center gap-2.5 rounded-xl bg-[#1B1D29] px-4 py-3 text-sm text-white shadow-[0_12px_32px_rgba(10,12,20,0.3)]">
                    {model.toast}
                </div>
            )}
        </>
    )
}

export default PolicyDrawer
